import React, { useEffect, useState } from 'react';
import { Controller, Control, FieldErrors, useWatch, Path } from 'react-hook-form';
import FormLabel from 'components/atoms/FormLabel';
import FormErrorLabel from 'components/atoms/FormErrorLabel';
import { StyledFormElementWrapper } from 'theme/styles';
import { IFormElementOptionProp, IFormElementProps } from 'models/form';
import { colors } from 'theme/colors';
import { InputAdornment, Box, ListSubheader } from '@mui/material';
import { EIconTypes } from 'constants/Icons';
import Icon from 'components/atoms/Icon/Icon';
import { useTranslations } from 'hooks/useTranslations';
import {
  StyledFormControl,
  StyledInnerWrapper,
  StyledMultiSelect,
  StyledSearchMenuItem,
  StyledSearchTextField,
  StyledClearIconButton,
  StyledClearButton,
  StyledSelectAllMenuItem,
  StyledCheckbox,
  StyledListItemText,
  StyledDivider,
  StyledSearchResultsMenuItem,
  StyledOptionMenuItem,
  getMenuProps,
} from './styles';
import { getNestedErrorMessage } from './helpers';

export type FormValues = {
  [key: string]: string[];
};

interface IFormSelect<T extends Record<string, any>> extends IFormElementProps {
  options: IFormElementOptionProp[];
  disabled?: boolean;
  required?: boolean;
  withValidation?: boolean;
  withLabel?: boolean;
  setValue: any;
  setValueAdditionalBehavior?: (value: any) => void;
  className?: string;
  canBeEmpty?: boolean;
  atLeastOneRequired?: boolean;
  arrayName?: string;
  arrayIndex?: number;
  fieldName?: string;
  onSelect?: (value: any) => void;
  translate?: boolean;
  searchable?: boolean;
  errors: FieldErrors<T>;
}

const toArray = (value: unknown): string[] => {
  if (Array.isArray(value)) return value;
  return [];
};

export const FormMultiSelect = <T extends Record<string, any>>({
  name,
  label,
  options,
  control,
  errors,
  disabled,
  required,
  className,
  setValue,
  setValueAdditionalBehavior,
  arrayName,
  arrayIndex,
  canBeEmpty = true,
  atLeastOneRequired = false,
  fieldName,
  withValidation = true,
  withLabel = true,
  onSelect,
  translate = false,
  searchable = true,
}: IFormSelect<T>) => {
  const { t } = useTranslations();
  const [error, setError] = useState<string>('');
  const showSelectAll = options.length > 5;
  const selectRef = React.useRef<HTMLDivElement>(null);
  const [searchText, setSearchText] = useState('');
  const [open, setOpen] = useState(false);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const optionsKey = options.map((o) => o.value).join(',');

  const watchedValue = useWatch({
    control,
    name: name as Path<T>,
  });

  const filteredOptions = React.useMemo(() => {
    if (!searchText) return options;
    return options.filter((option) =>
      (translate ? t(option.label) : option.label)
        .toLowerCase()
        .includes(searchText.toLowerCase()),
    );
  }, [searchText, options, translate, t]);

  useEffect(() => {
    if (open && searchInputRef.current && document.activeElement !== searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [filteredOptions, open]);

  useEffect(() => {
    if (searchText) {
      setSearchText('');
    }
  }, [optionsKey]);

  useEffect(() => {
    if (!options.length) return;
    const currentValues = toArray(watchedValue);
    if (!currentValues.length) return;

    const validValues = new Set(options.map((o) => String(o.value)));
    const filtered = currentValues.filter((v) => validValues.has(v));

    if (filtered.length !== currentValues.length) {
      setValue(name, filtered, {
        shouldValidate: true,
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [optionsKey, watchedValue, name, setValue]);

  useEffect(() => {
    let isError = false;
    if (name.includes('.') && !name.includes('[') && Object.keys(errors).length) {
      const keys = name.split('.');
      const errorMessage = getNestedErrorMessage(errors, keys);
      if (keys && errorMessage) {
        isError = true;
        setError(errorMessage);
      }
    } else {
      const errorMessage =
        arrayName && arrayIndex !== undefined && fieldName
          ? // @ts-expect-error: required by update
            (errors?.[arrayName]?.[arrayIndex]?.[fieldName]?.message as string | undefined)
          : (errors[name]?.message as string | undefined);

      if (errorMessage) {
        isError = true;
        setError(errorMessage);
      }
    }
    if (!isError) setError('');
  }, [errors, name, arrayName, arrayIndex, fieldName]);

  const handleSelectAll = (
    currentSelection: string[],
    onChange: (value: string[]) => void,
  ) => {
    const allValues = options.map((o) => String(o.value));
    if (currentSelection.length === options.length) {
      if (atLeastOneRequired) return;
      onChange([]);
      if (setValueAdditionalBehavior) setValueAdditionalBehavior([]);
    } else {
      onChange(allValues);
      if (setValueAdditionalBehavior) setValueAdditionalBehavior(allValues);
    }
  };

  return (
    <StyledFormElementWrapper className={className}>
      {withLabel && (
        <FormLabel name={name} label={label} disabled={disabled} required={required} />
      )}
      <StyledInnerWrapper>
        <Controller<T>
          name={name as any}
          control={control}
          render={({ field }) => (
            <StyledFormControl
              ref={selectRef}
              className={className}
              error={!!errors[name]?.message}
            >
              <StyledMultiSelect
                key={options.length}
                {...field}
                fullWidth
                value={toArray(field.value)}
                displayEmpty
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => {
                  setOpen(false);
                  setTimeout(() => setSearchText(''), 200);
                }}
                labelId={`${name}-label`}
                multiple
                renderValue={(selected) => {
                  const selectedValues = toArray(selected);
                  if (!selectedValues || selectedValues.length === 0) {
                    return t('formSelect.select.placeholder');
                  }
                  if (selectedValues.length === 1) {
                    const option = options.find((o) => String(o.value) === selectedValues[0]);
                    if (!option) return selectedValues[0];
                    return translate ? t(option.label) : option.label;
                  }
                  return `${selectedValues.length} ${t('formSelect.selected.text')}`;
                }}
                endAdornment={
                  toArray(field.value).length && canBeEmpty && !disabled ? (
                    <StyledClearButton
                      onClick={() => {
                        setValue(name, []);
                        if (setValueAdditionalBehavior) setValueAdditionalBehavior([]);
                      }}
                      id="clear"
                    >
                      <Icon
                        type={EIconTypes.clear}
                        color={disabled ? colors.gray400 : colors.gray900}
                      />
                    </StyledClearButton>
                  ) : null
                }
                id={name}
                disabled={disabled}
                error={!!error}
                onChange={(e) => {
                  let value = e.target.value as string[];
                  if (!Array.isArray(value)) value = [];
                  field.onChange(value);
                  if (onSelect) onSelect(value);
                  if (setValueAdditionalBehavior) setValueAdditionalBehavior(value);
                }}
                sx={{
                  ...((!field.value || toArray(field.value).length === 0) && {
                    color: colors.gray400,
                  }),
                }}
                MenuProps={getMenuProps(selectRef.current?.offsetWidth)}
              >
                {searchable && (
                  <StyledSearchMenuItem
                    key={`search-field_${name}`}
                    disableRipple
                    disableTouchRipple
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <StyledSearchTextField
                      inputRef={searchInputRef}
                      placeholder={`${t('formSelect.search.placeholder')}...`}
                      fullWidth
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                      }}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              {searchText ? (
                                <StyledClearIconButton onClick={() => setSearchText('')}>
                                  <Icon type={EIconTypes.clear} color={colors.gray900} />
                                </StyledClearIconButton>
                              ) : (
                                <Icon type={EIconTypes.search} color={colors.gray900} />
                              )}
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </StyledSearchMenuItem>
                )}
                {showSelectAll && !searchText && (
                  <div>
                    <StyledSelectAllMenuItem
                      key={`select-all_${name}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSelectAll(toArray(field.value), field.onChange);
                      }}
                    >
                      <StyledCheckbox
                        checked={toArray(field.value)?.length === options.length}
                        icon={
                          <Icon
                            type={EIconTypes.squareCheckboxUnchecked}
                            color={colors.gray300}
                            fontSize={16}
                            viewBox="0 0 16 16"
                          />
                        }
                        checkedIcon={
                          <Icon
                            type={EIconTypes.squareCheckboxChecked}
                            color={colors.primary700}
                            fontSize={16}
                            viewBox="0 0 16 16"
                          />
                        }
                      />
                      <StyledListItemText primary={t('formSelect.selectAll.text')} />
                    </StyledSelectAllMenuItem>
                    <StyledDivider />
                  </div>
                )}
                {searchText && filteredOptions.length > 0 && (
                  <StyledSearchResultsMenuItem disabled>
                    {t('formSelect.searchResults.text').toUpperCase()}
                  </StyledSearchResultsMenuItem>
                )}
                <ListSubheader component="div" disableGutters disableSticky>
                  <Box
                    sx={{
                      maxHeight: '268px',
                      overflowY: 'auto',
                      '&::-webkit-scrollbar': {
                        width: '12px',
                        borderRight: '4px solid transparent',
                        backgroundClip: 'padding-box',
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: colors.primary50,
                        borderRadius: '4px',
                        marginTop: '4px',
                        marginBottom: '4px',
                        borderRight: '4px solid transparent',
                        backgroundClip: 'padding-box',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: colors.primary400,
                        borderRadius: '4px',
                        border: `2px solid ${colors.primary50}`,
                        borderRight: '4px solid transparent',
                        backgroundClip: 'padding-box',
                      },
                      '&::-webkit-scrollbar-thumb:hover': {
                        backgroundColor: colors.primary400,
                      },
                      '&::-webkit-scrollbar-button': { display: 'none' },
                    }}
                  >
                    {filteredOptions.map((option) => {
                      const value = String(option.value);
                      const currentValue = toArray(field.value);
                      const checked = currentValue.includes(value);
                      return (
                        <StyledOptionMenuItem
                          key={`option_${value}`}
                          value={value}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (atLeastOneRequired && checked && currentValue.length === 1)
                              return;
                            const newValue = checked
                              ? currentValue.filter((v) => v !== value)
                              : [...currentValue, value];
                            field.onChange(newValue);
                            if (onSelect) onSelect(newValue);
                            if (setValueAdditionalBehavior)
                              setValueAdditionalBehavior(newValue);
                          }}
                        >
                          <StyledCheckbox
                            checked={checked}
                            icon={
                              <Icon
                                type={EIconTypes.squareCheckboxUnchecked}
                                color={colors.gray300}
                                fontSize={16}
                                viewBox="0 0 16 16"
                              />
                            }
                            checkedIcon={
                              <Icon
                                type={EIconTypes.squareCheckboxChecked}
                                color={colors.primary700}
                                fontSize={16}
                                viewBox="0 0 16 16"
                              />
                            }
                          />
                          <StyledListItemText
                            primary={translate ? t(option.label) : option.label}
                          />
                        </StyledOptionMenuItem>
                      );
                    })}
                  </Box>
                </ListSubheader>
              </StyledMultiSelect>
            </StyledFormControl>
          )}
        />
      </StyledInnerWrapper>
      {withValidation && <FormErrorLabel label={error} />}
    </StyledFormElementWrapper>
  );
};
